import { useEffect, useState } from 'react';
import { UserRound, Shield, SlidersHorizontal } from 'lucide-react';
import { Modal, ErrorNotice, Spinner } from './components';
import { ProfileForm, type AccountState, type User } from './AccountJourney';
import { AccountSettings } from './AccountSecurity';
import { AppearanceSettings, defaultAppearance, type Appearance } from './AppearanceSettings';
import { api } from './api';

export function WorkbenchAccount({ initialSection = 'profile', appearance, onClose, onSaved }: {
  initialSection?: string; appearance: Appearance; onClose: () => void; onSaved: () => void;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [tab, setTab] = useState(initialSection), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const load = () => { setError(''); api<AccountState>('/account/session').then((state) => { setUser(state.user); if (!state.user) setError('Your session has ended. Sign in again to manage your account.'); }).catch((e) => setError(e.message)); };
  useEffect(load, []);
  return <Modal title="Profile and account" onClose={() => { if (!busy) onClose(); }}>
    <div className="desktop-account modern-account web-account-dialog">
      <div className="account-settings-layout">
        <aside className="account-sidebar"><nav className="account-section-nav" aria-label="Account settings">
          {([['profile', 'Profile', UserRound], ['security', 'Security', Shield], ['appearance', 'Appearance', SlidersHorizontal]] as const).map(([id, name, Icon]) =>
            <button key={id} disabled={busy} aria-current={tab === id ? 'page' : undefined} onClick={() => setTab(id)}><Icon size={16}/>{name}</button>)}
        </nav></aside>
        <div className="account-main-panel">
          {error && <ErrorNotice message={error} retry={load}/>}
          <div hidden={tab !== 'appearance'}><AppearanceSettings initial={appearance || defaultAppearance}/></div>
          {!user && tab !== 'appearance' && !error && <Spinner label="Opening your account…"/>}
          {user && <>
            <div hidden={tab !== 'profile'}><ProfileForm key={version} user={user} embedded onBusyChange={setBusy} onCancel={() => setVersion(v => v + 1)} onSaved={next => { setUser(next); onSaved(); }}/></div>
            <div hidden={tab !== 'security'}><AccountSettings user={user} desktop onBusyChange={setBusy} onChanged={setUser}
              onVerified={state => { if (state.user) setUser(state.user); }} onEnded={() => location.assign('/signin')}/>
              <button className="button secondary" disabled={busy} onClick={async () => { setBusy(true); try { await api('/account/logout', 'POST', {}); location.assign('/signin'); } catch (e) { setError((e as Error).message); } finally { setBusy(false); } }}>Sign out</button>
            </div>
          </>}
        </div>
      </div>
    </div>
    <div className="workbench-dialog-footer"><button className="primary" disabled={busy} onClick={onClose}>Done</button></div>
  </Modal>;
}
